import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import * as ErrorReporting from 'services/error-reporting/error-reporting'
import { Button, Heading, Text } from 'components'
import { PlatformIcon } from 'components/PlatformIcon'
import { Textarea } from 'components/Textarea'
import { useAnalytics } from 'hooks'
import { cn } from 'lib/utils'
import { ArrowLeft, Check, Loader2 } from 'lucide-react'
import { useLinkedInConnect } from 'modules/oauth/hooks/useLinkedInConnect'
import { oauthUtils } from 'modules/oauth/utils/oauth-utils'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { IntegrationsService } from 'openapi/services/IntegrationsService'
import type { UpdateAutopilotDto } from 'openapi/models/UpdateAutopilotDto'
import { useBrandKitStore } from '../../../creative/store/brand-kit-store'
import { useAutopilotStore } from '../../store/autopilot-store'
import { useWeeklyQueueStore } from '../../store/weekly-queue-store'
import type {
  Autopilot,
  AutopilotChannel,
  AutopilotDirection,
  AutopilotFrequency,
  AutopilotPublishMode,
} from '../../types'
import { useConnectedPlatforms } from '../../hooks/useConnectedPlatforms'
import {
  AUTOPILOT_CHANNEL_OPTIONS,
  AUTOPILOT_DIRECTION_OPTIONS,
  AUTOPILOT_FREQUENCY_OPTIONS,
  CONNECTABLE_CHANNEL_LABEL,
  PRESET_DAYS,
  WEEKDAY_OPTIONS,
  daysSummary,
  formatTimeLabel,
  frequencyFromDays,
  isConnectableChannel,
  sortWeekdays,
} from '../../utils/weekly-queue-utils'

/** Display label for an autopilot channel, falling back to the raw id. */
function channelLabelFor(channel: AutopilotChannel): string {
  return isConnectableChannel(channel) ? CONNECTABLE_CHANNEL_LABEL[channel] : channel
}

// ─── Prefill helpers (edit mode reads from the existing plan) ────────────────
function initialDirections(plan?: Autopilot): AutopilotDirection[] {
  const raw = plan?.contentSpecifics?.directions
  if (Array.isArray(raw) && raw.length > 0) return raw as AutopilotDirection[]
  if (plan?.contentDirection) return [plan.contentDirection]
  return ['expertise']
}
function initialChannels(plan?: Autopilot): AutopilotChannel[] {
  const raw = plan?.contentSpecifics?.channels
  if (Array.isArray(raw) && raw.length > 0) return raw as AutopilotChannel[]
  if (plan?.channel) return [plan.channel]
  return ['instagram']
}
function initialDays(plan?: Autopilot): string[] {
  if (plan?.preferredDays && plan.preferredDays.length > 0) return sortWeekdays(plan.preferredDays)
  if (plan?.frequency && plan.frequency !== 'custom') return PRESET_DAYS[plan.frequency]
  return PRESET_DAYS['3x_weekly']
}

type Step = 'what' | 'when' | 'how' | 'where'
const STEP_ORDER: Step[] = ['what', 'when', 'how', 'where']

interface WeeklyPlanWizardProps {
  /** Business id required to scope the OAuth callback on the "where" step. */
  businessId: string
  /** When set, the wizard edits this plan in place instead of creating one. */
  existingPlan?: Autopilot
  /** Return to the intro card (Cancel / back off the first step). */
  onCancel: () => void
  /** Plan created or saved — let the parent refresh + leave the wizard. */
  onCreated: () => void
}

/**
 * Inline four-step setup for the weekly plan — what / when / how / where.
 * Reused for both first-time setup (create) and editing an existing plan
 * (`existingPlan` set → "Save changes" + PATCH). The "when" step picks the
 * exact weekdays (`preferredDays`) and publish time; the "where" step requires
 * connecting each chosen channel before the plan turns on.
 *
 * In create mode it renders inside the empty-state card shell owned by
 * WeeklyCockpit; in edit mode it's mounted inside a Dialog.
 */
export function WeeklyPlanWizard({
  businessId,
  existingPlan,
  onCancel,
  onCreated,
}: WeeklyPlanWizardProps) {
  const isEdit = !!existingPlan
  const { track } = useAnalytics()
  const createPlan = useAutopilotStore(s => s.createPlan)
  const updatePlan = useAutopilotStore(s => s.updatePlan)
  const fetchQueue = useWeeklyQueueStore(s => s.fetchQueue)
  const brandTimezone = useBrandKitStore(s => s.brandKit.timezone)
  const {
    connectedProviders,
    refetch: refetchConnectedPlatforms,
    loading: connectedLoading,
  } = useConnectedPlatforms()
  const { resolveConnectProvider, chooser } = useLinkedInConnect()

  const [step, setStep] = useState<Step>('what')
  const [directions, setDirections] = useState<AutopilotDirection[]>(() =>
    initialDirections(existingPlan)
  )
  const [customDescription, setCustomDescription] = useState<string>(
    () => (existingPlan?.contentSpecifics?.description as string | undefined) ?? ''
  )
  const [preferredDays, setPreferredDays] = useState<string[]>(() => initialDays(existingPlan))
  const [preferredTime, setPreferredTime] = useState<string>(
    () => existingPlan?.preferredTime ?? '09:00'
  )
  // Default to review-first: posts wait for the user's approval. Fully automatic
  // is an explicit opt-in.
  const [publishMode, setPublishMode] = useState<AutopilotPublishMode>(
    () => existingPlan?.publishMode ?? 'review'
  )
  const [channels, setChannels] = useState<AutopilotChannel[]>(() => initialChannels(existingPlan))
  // A NEW plan defaults to EVERY connected platform it can publish to — users
  // expect the weekly plan to cover their whole presence, and the old
  // instagram-only default read as "Taka isn't posting" on the other connected
  // platforms (the Estrada case). Connections load async, so this runs once
  // they arrive; the moment the user touches the picker it stops steering.
  const userTouchedChannelsRef = useRef(false)
  useEffect(() => {
    if (isEdit || userTouchedChannelsRef.current || connectedLoading) return
    const connected = AUTOPILOT_CHANNEL_OPTIONS.map(option => option.id).filter(id =>
      connectedProviders.includes(id)
    ) as AutopilotChannel[]
    // Merge (not replace): the seeded 'instagram' stays in `channels` even when
    // unconnected, and selection already requires a live connection downstream.
    if (connected.length > 0) {
      setChannels(prev => Array.from(new Set([...prev, ...connected])))
    }
  }, [isEdit, connectedLoading, connectedProviders])
  // Which channel's OAuth popup is currently open (null = none). Connects run
  // one at a time, so a single id is enough to scope the per-row spinner.
  const [connectingChannel, setConnectingChannel] = useState<AutopilotChannel | null>(null)
  const [connectError, setConnectError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  // Monotonic token for the in-flight OAuth attempt. An abandoned popup can hold
  // the connect promise open for ~30s (closed-popup Pusher grace + persistence
  // wait) with every chip disabled — Cancel bumps the token to unlock the step
  // NOW, and the stale attempt's late resolution is dropped instead of clobbering
  // whatever the user did next.
  const connectAttemptRef = useRef(0)

  // Set true once the wizard finishes (create/save) or the user explicitly
  // cancels, so the unmount cleanup doesn't double-count a genuine completion as
  // an abandonment. `stepRef` keeps the latest step readable from the unmount
  // cleanup (which captures stale closure state otherwise).
  const completedRef = useRef(false)
  const stepRef = useRef(step)
  stepRef.current = step

  const stepIndex = STEP_ORDER.indexOf(step)

  // Funnel: one event per step the user lands on.
  useEffect(() => {
    track(AnalyticsEvents.Autopilot.AUTOPILOT_WIZARD_STEP_VIEWED, {
      step,
      step_index: STEP_ORDER.indexOf(step),
      is_edit: isEdit,
    })
  }, [step, isEdit, track])

  // Drop-off: if the wizard unmounts before completion/cancel, report where the
  // user was. Empty deps so this runs once on unmount with refs for fresh state.
  useEffect(() => {
    return () => {
      if (completedRef.current) return
      track(AnalyticsEvents.Autopilot.AUTOPILOT_WIZARD_ABANDONED, {
        step: stepRef.current,
        step_index: STEP_ORDER.indexOf(stepRef.current),
        is_edit: isEdit,
        reason: 'unmount',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  // Custom direction needs free text — block advancing until it's filled.
  const customSelected = directions.includes('custom')
  const customMissing = customSelected && !customDescription.trim()
  // Selection now implies connection: a channel only counts when it's both in
  // the plan and currently connected. A saved-but-since-disconnected channel
  // drops out until the user re-connects it from its pill.
  const selectedChannels = channels.filter(c => connectedProviders.includes(c))
  const canSubmit = selectedChannels.length > 0
  // The preset chip to highlight is derived from the current weekday set.
  const activePreset = frequencyFromDays(preferredDays)

  const toggleDirection = useCallback((id: AutopilotDirection) => {
    setDirections(prev => {
      if (prev.includes(id)) {
        // Keep at least one — autopilot needs a direction to generate against.
        if (prev.length === 1) return prev
        return prev.filter(d => d !== id)
      }
      return [...prev, id]
    })
  }, [])

  const toggleDay = useCallback((id: string) => {
    setPreferredDays(prev => {
      if (prev.includes(id)) {
        // Keep at least one day — a plan needs somewhere on the week to post.
        if (prev.length === 1) return prev
        return sortWeekdays(prev.filter(d => d !== id))
      }
      return sortWeekdays([...prev, id])
    })
  }, [])

  const selectPreset = useCallback((id: AutopilotFrequency) => {
    // 'custom' keeps whatever days are selected — the grid drives it.
    if (id === 'custom') return
    setPreferredDays(PRESET_DAYS[id])
  }, [])

  const goNext = useCallback(() => {
    setStep(prev => STEP_ORDER[Math.min(STEP_ORDER.length - 1, STEP_ORDER.indexOf(prev) + 1)])
  }, [])

  const goBack = useCallback(() => {
    if (stepIndex === 0) {
      // Cancel off the first step is a genuine drop-off — mark completed so the
      // unmount cleanup doesn't fire a second (unmount) abandon for the same exit.
      completedRef.current = true
      track(AnalyticsEvents.Autopilot.AUTOPILOT_WIZARD_ABANDONED, {
        step,
        step_index: stepIndex,
        is_edit: isEdit,
        reason: 'cancel',
      })
      onCancel()
      return
    }
    setStep(STEP_ORDER[stepIndex - 1])
  }, [stepIndex, step, isEdit, onCancel, track])

  /**
   * Single pill control: connect + select in one tap.
   * - Selected (in plan & connected) → toggle off, keep the connection.
   * - Connected but not selected → select instantly, no OAuth.
   * - Not connected → run OAuth; only select on a verified connection.
   */
  const handleChannelClick = useCallback(
    async (channel: AutopilotChannel) => {
      userTouchedChannelsRef.current = true
      setConnectError(null)
      const isConnected = connectedProviders.includes(channel)
      // Toggle off a live selection — the channel stays connected for next time.
      if (isConnected && channels.includes(channel)) {
        setChannels(prev => prev.filter(c => c !== channel))
        return
      }
      // Already connected → just add it to the plan, no popup.
      if (isConnected) {
        setChannels(prev => (prev.includes(channel) ? prev : [...prev, channel]))
        return
      }
      // Not connected yet → run the OAuth flow and only select on success. Every
      // await below re-checks the attempt token: the user may have hit Cancel
      // (unlocking the step) while this attempt was still waiting on the popup.
      if (!isConnectableChannel(channel)) return
      // LinkedIn fronts two OAuth apps (personal `linkedin` / company `linkedin_org`).
      // Open the shared chooser first (when gated on); null = user dismissed → abort
      // before we touch the spinner. Every other channel passes through unchanged.
      // Both LinkedIn variants land on the single `linkedin` row, so all the
      // verify/refetch below stays keyed on the original `channel`.
      const oauthProvider = await resolveConnectProvider(channel)
      if (!oauthProvider) return
      const channelLabel = channelLabelFor(channel)
      const attempt = ++connectAttemptRef.current
      const stale = () => connectAttemptRef.current !== attempt
      setConnectingChannel(channel)
      try {
        const result = await oauthUtils.openOAuthPopup(oauthProvider, {
          businessId,
          source: 'autopilot',
          track,
        })
        if (stale()) return
        if (result === false) {
          setConnectError('Connection was cancelled or failed. Please try again.')
          return
        }
        // result === true | null — wait for the server to finish persisting the
        // connection (the popup self-closes before the DB write + OAuthUpdated
        // Pusher event land), then verify against the server before celebrating.
        // Without this wait the status check races the ~800ms persistence gap and
        // surfaces a false "We couldn't verify the connection." on a real success.
        await oauthUtils.waitForOAuthPersistence(channel)
        if (stale()) return
        const status =
          await IntegrationsService.integrationControllerGetIntegrationStatusV1(channel)
        if (stale()) return
        if (status.isConnected) {
          await refetchConnectedPlatforms()
          if (stale()) return
          setChannels(prev => (prev.includes(channel) ? prev : [...prev, channel]))
        } else {
          setConnectError('We couldn’t verify the connection. Please try again.')
        }
      } catch (error) {
        ErrorReporting.captureException(error, {
          tags: { feature: 'weekly_plan_wizard', step: 'connect_oauth', channel },
          extra: { businessId },
        })
        console.error('WeeklyPlanWizard: OAuth flow failed', error)
        if (!stale()) setConnectError(`Something went wrong connecting to ${channelLabel}.`)
      } finally {
        if (!stale()) setConnectingChannel(null)
      }
    },
    [
      channels,
      connectedProviders,
      businessId,
      track,
      refetchConnectedPlatforms,
      resolveConnectProvider,
    ]
  )

  // Escape hatch for an abandoned OAuth popup: unlock the chips immediately and
  // let the in-flight attempt's late resolution fall on the floor.
  const handleCancelConnect = useCallback(() => {
    connectAttemptRef.current++
    setConnectingChannel(null)
    setConnectError(null)
  }, [])

  const handleSubmit = useCallback(async () => {
    if (submitting || selectedChannels.length === 0) return
    setSubmitting(true)
    const frequency = frequencyFromDays(preferredDays)
    const description = customDescription.trim()
    try {
      if (isEdit && existingPlan) {
        const patch: UpdateAutopilotDto = {
          channel: selectedChannels[0],
          contentDirection: directions[0],
          contentSpecifics: {
            ...(existingPlan.contentSpecifics ?? {}),
            directions,
            channels: selectedChannels,
            lastDirectionIndex: 0,
            ...(description ? { description } : {}),
          },
          frequency,
          preferredDays,
          preferredTime,
          publishMode,
          // Flipping review → automatic means "publish my week without me":
          // also release the already-created posts from the approval gate,
          // or the current week keeps silently missing its slots while only
          // NEXT week's posts behave as the user expects.
          ...(publishMode === 'automatic' &&
            existingPlan.publishMode === 'review' && { releasePendingPosts: true }),
        }
        await updatePlan(existingPlan.id, patch)
        track(AnalyticsEvents.Autopilot.AUTOPILOT_PLAN_FIELD_UPDATED, {
          field: 'wizard_edit',
          value: `${frequency}:${preferredDays.length}d`,
        })
      } else {
        // "Autopilot Plan Created" (carrying channel, frequency, publish_mode,
        // etc.) is emitted server-side in AutopilotService.create. Mode at
        // creation is captured by that event's publish_mode property, so no
        // separate client mode-changed event is needed here.
        await createPlan({
          channels,
          directions,
          frequency,
          preferredDays,
          preferredTime,
          customDescription,
          publishMode,
        })
      }
      // Refresh the queue so the cockpit reflects the new plan/cadence as soon
      // as the kickoff (or reschedule) lands.
      void fetchQueue()
      // Successful create/save — suppress the unmount-abandon. Completion itself
      // is tracked by Autopilot Plan Created (create) / Plan Field Updated (edit).
      completedRef.current = true
      onCreated()
    } catch (error) {
      ErrorReporting.captureException(error, {
        tags: { feature: 'weekly_plan_wizard', step: isEdit ? 'update_plan' : 'create_plan' },
        extra: { businessId, frequency, directions, channels: selectedChannels },
      })
      console.error('WeeklyPlanWizard: failed to save plan', error)
      toast.error(
        isEdit
          ? 'Couldn’t save your changes. Try again.'
          : 'Couldn’t turn on your weekly plan. Try again.'
      )
    } finally {
      setSubmitting(false)
    }
  }, [
    submitting,
    selectedChannels,
    isEdit,
    existingPlan,
    createPlan,
    updatePlan,
    directions,
    customDescription,
    preferredDays,
    preferredTime,
    publishMode,
    track,
    fetchQueue,
    onCreated,
    businessId,
  ])

  const stepCopy = useMemo(
    () => ({
      what: {
        title: 'What should Taka post about?',
        hint: 'Pick one or more. Taka rotates between them so your feed stays varied.',
      },
      when: {
        title: 'How often, and when?',
        hint: 'Pick the days and the time. You can change this anytime.',
      },
      how: {
        title: 'How should Taka publish?',
        hint: 'You can switch this anytime in plan settings.',
      },
      where: {
        title: 'Where should it post?',
        hint: 'Pick one or more. Taka posts the same update to each.',
      },
    }),
    []
  )

  return (
    <div className="relative mx-auto max-w-md text-left">
      {chooser}
      {/* Step indicator */}
      <div className="mb-5 flex items-center justify-center gap-1.5" aria-hidden>
        {STEP_ORDER.map((s, i) => (
          <span
            key={s}
            className={cn(
              'h-1.5 rounded-full transition-all',
              i === stepIndex ? 'w-6 bg-acid' : i < stepIndex ? 'w-3 bg-acid/50' : 'w-3 bg-line'
            )}
          />
        ))}
      </div>

      <Heading level="h3" className="text-center">
        {stepCopy[step].title}
      </Heading>
      <Text
        variant="body"
        tone="paper-dim"
        as="p"
        className="mx-auto mt-1.5 mb-5 max-w-sm text-center"
      >
        {stepCopy[step].hint}
      </Text>

      {step === 'what' && (
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-2">
            {AUTOPILOT_DIRECTION_OPTIONS.map(({ id, emoji, label }) => {
              const on = directions.includes(id)
              return (
                <button
                  key={id}
                  type="button"
                  aria-pressed={on}
                  onClick={() => toggleDirection(id)}
                  className={cn(
                    'flex items-center gap-2 rounded-[10px] border px-3 py-2.5 text-left text-sm transition-colors',
                    on
                      ? 'bg-acid-tint border-acid font-medium text-paper'
                      : 'border-line bg-bg-2 text-paper-dim hover:border-line-bright hover:text-paper'
                  )}
                >
                  <span className="text-base leading-none">{emoji}</span>
                  <span className="truncate">{label}</span>
                </button>
              )
            })}
          </div>

          {/* Custom direction needs free text — the server's `custom` direction
              reads this from contentSpecifics.description. */}
          {customSelected && (
            <Textarea
              variant="dark"
              value={customDescription}
              onChange={e => setCustomDescription(e.target.value)}
              placeholder="Describe what Taka should post, e.g. “A Friday tip about saving time with our tool.”"
              rows={3}
              className="text-sm"
              autoFocus
            />
          )}
        </div>
      )}

      {step === 'when' && (
        <div className="flex flex-col gap-4">
          {/* Cadence presets — pre-fill the weekday set below. */}
          <div className="flex flex-wrap justify-center gap-2">
            {AUTOPILOT_FREQUENCY_OPTIONS.map(({ id, label }) => {
              const on = activePreset === id
              return (
                <button
                  key={id}
                  type="button"
                  aria-pressed={on}
                  onClick={() => selectPreset(id)}
                  className={cn(
                    'rounded-full border px-3.5 py-1.5 text-sm transition-colors',
                    on
                      ? 'bg-acid-tint border-acid font-medium text-paper'
                      : 'border-line bg-bg-2 text-paper-dim hover:border-line-bright hover:text-paper'
                  )}
                >
                  {label}
                </button>
              )
            })}
          </div>

          {/* Weekday grid — the authoritative schedule. Number of days = posts/week. */}
          <div>
            <p className="mb-1.5 text-[10px] font-semibold tracking-wide text-paper-dim uppercase">
              Which days?
            </p>
            <div className="grid grid-cols-7 gap-1.5">
              {WEEKDAY_OPTIONS.map(({ id, short, label }) => {
                const on = preferredDays.includes(id)
                return (
                  <button
                    key={id}
                    type="button"
                    aria-pressed={on}
                    aria-label={label}
                    title={label}
                    onClick={() => toggleDay(id)}
                    className={cn(
                      'flex h-9 items-center justify-center rounded-[8px] border text-sm font-medium transition-colors',
                      on
                        ? 'bg-acid-tint border-acid text-paper'
                        : 'border-line bg-bg-2 text-paper-dim hover:border-line-bright hover:text-paper'
                    )}
                  >
                    {short}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Publish time */}
          <div>
            <p className="mb-1.5 text-[10px] font-semibold tracking-wide text-paper-dim uppercase">
              What time?
            </p>
            <input
              type="time"
              value={preferredTime}
              onChange={e => {
                const next = e.target.value
                // Native time inputs emit '' mid-edit; only commit valid HH:mm.
                if (/^([01]\d|2[0-3]):[0-5]\d$/.test(next)) setPreferredTime(next)
              }}
              className="w-full rounded-[10px] border border-line bg-bg-2 px-3 py-2 text-sm text-paper transition-colors outline-none focus:border-acid"
            />
          </div>

          <p className="text-center text-xs text-paper-dim">
            Taka posts {daysSummary(preferredDays)} at {formatTimeLabel(preferredTime)}
            {brandTimezone ? ` · ${brandTimezone}` : ' · your local time'}.
          </p>
        </div>
      )}

      {step === 'how' && (
        <div className="flex flex-col gap-2.5">
          {[
            {
              id: 'review' as AutopilotPublishMode,
              emoji: '👀',
              label: 'Review first',
              desc: 'Taka drafts each post and waits for your approval before it goes live.',
            },
            {
              id: 'automatic' as AutopilotPublishMode,
              emoji: '⚡',
              label: 'Fully automatic',
              desc: 'Taka publishes on schedule. No approval needed.',
            },
          ].map(({ id, emoji, label, desc }) => {
            const on = publishMode === id
            return (
              <button
                key={id}
                type="button"
                data-testid={`publish-mode-${id}`}
                aria-pressed={on}
                onClick={() => setPublishMode(id)}
                className={cn(
                  'flex items-start gap-3 rounded-[12px] border px-4 py-3 text-left transition-colors',
                  on
                    ? 'bg-acid-tint border-acid text-paper'
                    : 'border-line bg-bg-2 text-paper-dim hover:border-line-bright hover:text-paper'
                )}
              >
                <span className="mt-0.5 text-lg leading-none">{emoji}</span>
                <span className="flex flex-col gap-0.5">
                  <span className="flex items-center gap-1.5 text-sm font-medium text-paper">
                    {label}
                    {on && <Check size={14} className="text-acid" />}
                  </span>
                  <span className="text-xs text-paper-dim">{desc}</span>
                </span>
              </button>
            )
          })}
        </div>
      )}

      {step === 'where' && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap justify-center gap-2">
            {AUTOPILOT_CHANNEL_OPTIONS.map(({ id, label }) => {
              // Selected only when both chosen and connected — the pill is the
              // connect-and-select control, so the acid state means "live".
              const selected = channels.includes(id) && connectedProviders.includes(id)
              const isConnecting = connectingChannel === id
              return (
                <button
                  key={id}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => handleChannelClick(id)}
                  disabled={connectingChannel !== null || connectedLoading}
                  className={cn(
                    'flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60',
                    selected
                      ? 'bg-acid-tint border-acid font-medium text-paper'
                      : 'border-line bg-bg-2 text-paper-dim hover:border-line-bright hover:text-paper'
                  )}
                >
                  <PlatformIcon channel={id} size={16} />
                  {label}
                  {isConnecting ? (
                    <Loader2 size={14} className="animate-spin text-acid" />
                  ) : selected ? (
                    <Check size={14} className="text-acid" />
                  ) : null}
                </button>
              )
            })}
          </div>

          {/* Live status while an OAuth popup is up: without it, an abandoned popup
              read as a silent lock (every chip disabled, no message, no way out). */}
          {connectingChannel && (
            <p
              role="status"
              className="flex items-center justify-center gap-2 text-center text-xs text-paper-dim"
            >
              <span>
                Connecting to {channelLabelFor(connectingChannel)}. Finish in the window that
                opened.
              </span>
              <button
                type="button"
                onClick={handleCancelConnect}
                className="text-paper underline underline-offset-2 hover:text-acid"
              >
                Cancel
              </button>
            </p>
          )}
          {connectError && <p className="text-center text-xs text-red-400">{connectError}</p>}
        </div>
      )}

      {/* Footer nav */}
      <div className="mt-6 flex items-center justify-between gap-3">
        <Button variant="t3-tertiary" size="t3-sm" onClick={goBack} disabled={submitting}>
          {stepIndex === 0 ? (
            'Cancel'
          ) : (
            <>
              <ArrowLeft size={14} />
              Back
            </>
          )}
        </Button>
        {step === 'where' ? (
          <Button
            variant="t3-primary"
            size="t3-md"
            onClick={handleSubmit}
            // Also parked while a connect is pending: submitting mid-connect would
            // silently exclude the channel the user is in the middle of adding.
            disabled={!canSubmit || submitting || connectingChannel !== null}
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
            {submitting
              ? isEdit
                ? 'Saving…'
                : 'Turning on…'
              : isEdit
                ? 'Save changes'
                : 'Turn on my plan'}
          </Button>
        ) : (
          <Button
            variant="t3-primary"
            size="t3-md"
            onClick={goNext}
            disabled={step === 'what' && customMissing}
          >
            Next
          </Button>
        )}
      </div>
    </div>
  )
}
